/**
 * Agent Transition Graph
 * 
 * Defines which agents are likely to be transitioned to from each agent.
 * Used for pre-warming connections to reduce transition latency.
 */

/**
 * Map of agent IDs to their likely next agents.
 * These connections will be pre-warmed in the background.
 */
export const AGENT_TRANSITION_GRAPH: Record<string, string[]> = {
  // Entry point - language detection, then welcome
  'greeting_agent': ['welcome_agent'],
  
  // Welcome agent - always goes to router
  'welcome_agent': ['router_agent'],
  
  // Router can go to any main flow
  'router_agent': [
    'faq_agent',
    'accident_start_agent', 
    'renewal_input_agent', 
    'lost_report_upload_extract_agent'
  ],
  
  // FAQ - can go to any flow (cross-flow navigation)
  'faq_agent': [
    'router_agent',
    'accident_start_agent',
    'renewal_input_agent',
    'lost_report_upload_extract_agent'
  ],
  
  // Accident flow - can go to other flows (cross-flow navigation)
  'accident_start_agent': [
    'accident_collect_submit_agent',
    'router_agent',
    'renewal_input_agent',
    'lost_report_upload_extract_agent',
    'faq_agent'
  ],
  'accident_collect_submit_agent': [
    'router_agent',
    'accident_start_agent',
    'renewal_input_agent',
    'lost_report_upload_extract_agent',
    'faq_agent'
  ],
  
  // Renewal flow - can go to other flows (cross-flow navigation)
  'renewal_input_agent': [
    'renewal_vehicle_agent',
    'router_agent',
    'accident_start_agent',
    'lost_report_upload_extract_agent',
    'faq_agent'
  ],
  'renewal_vehicle_agent': [
    'renewal_confirm_agent',
    'router_agent',
    'accident_start_agent',
    'lost_report_upload_extract_agent',
    'faq_agent'
  ],
  'renewal_confirm_agent': [
    'renewal_result_agent',
    'router_agent',
    'accident_start_agent',
    'lost_report_upload_extract_agent',
    'faq_agent'
  ],
  'renewal_result_agent': [
    'router_agent',
    'renewal_input_agent',
    'accident_start_agent',
    'lost_report_upload_extract_agent',
    'faq_agent'
  ],
  
  // Lost report flow - can go to other flows (cross-flow navigation)
  'lost_report_upload_extract_agent': [
    'lost_report_search_agent',
    'router_agent',
    'accident_start_agent',
    'renewal_input_agent',
    'faq_agent'
  ],
  'lost_report_search_agent': [
    'lost_report_result_agent',
    'router_agent',
    'accident_start_agent',
    'renewal_input_agent',
    'faq_agent'
  ],
  'lost_report_result_agent': [
    'router_agent',
    'lost_report_upload_extract_agent',
    'accident_start_agent',
    'renewal_input_agent',
    'faq_agent'
  ],
};

/**
 * Get the list of likely next agents for a given agent.
 * Returns empty array if agent is not in the graph.
 * 
 * @param currentAgentId - The current agent's ID
 * @returns Array of agent IDs that are likely to be transitioned to
 */
export function getLikelyNextAgents(currentAgentId: string): string[] {
  return AGENT_TRANSITION_GRAPH[currentAgentId] || [];
}

/**
 * Check if an agent has likely next agents defined.
 * 
 * @param agentId - The agent ID to check
 * @returns true if the agent has pre-warm targets defined
 */
export function hasPrewarmTargets(agentId: string): boolean {
  const targets = AGENT_TRANSITION_GRAPH[agentId];
  return targets !== undefined && targets.length > 0;
}

/**
 * Get all unique agent IDs from the transition graph.
 * Useful for validation and debugging.
 */
export function getAllAgentsInGraph(): string[] {
  const allAgents = new Set<string>();
  
  // Add all source agents
  Object.keys(AGENT_TRANSITION_GRAPH).forEach(agent => allAgents.add(agent));
  
  // Add all target agents
  Object.values(AGENT_TRANSITION_GRAPH).forEach(targets => {
    targets.forEach(target => allAgents.add(target));
  });
  
  return Array.from(allAgents);
}

